/**
 * Model Downloader
 * 
 * Downloads model node data and stores in OPFS.
 * 
 * Download modes:
 * 1. Row Pagination (default for API) - Parallel requests with X-Row-Range headers
 *    Server only queries requested rows, most efficient for API endpoints.
 * 2. Range requests (for blob storage) - Multiple requests with byte ranges
 *    Efficient for pre-serialized files in Azure Blob Storage or S3.
 * 
 * The blob storage provider is determined by feature flags and configuration.
 * Use `configureBlobStorageFromFeatures()` at app init to set the provider.
 */

import {
  deleteModelFile,
  getModelFileSize,
  getOPFSRoot,
  isModelCached,
  readModelFile,
  writeModelFile,
} from '../storage/opfsStorage';

import {
  getBlobStorageProvider,
  getBlobStorageService,
  type BlobDownloadProgress,
  type BlobStorageProvider,
  type NodeBlobManifest,
} from '../storage/blobStorage';

// Chunk sizes for Range requests
// Balance between progress update frequency and HTTP overhead (connection setup, headers, round trips)
// Larger chunks = fewer HTTP requests = faster downloads, but less frequent progress updates

// Azure Blob Storage: 10MB balances progress updates with efficiency
// For 200MB file: 20 chunks, ~5 rounds with 4 parallel = ~250ms RTT overhead
const AZURE_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

// S3: 5MB aligns with S3 multipart upload part size (minimum 5MB per part)
const S3_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

// Default chunk size (used when not specified)
const DEFAULT_CHUNK_SIZE = AZURE_CHUNK_SIZE;

/**
 * Gets the optimal chunk size based on the storage provider.
 */
function getOptimalChunkSize(provider: BlobStorageProvider): number {
  switch (provider) {
    case 'azure':
      return AZURE_CHUNK_SIZE;
    case 's3':
      return S3_CHUNK_SIZE;
    default:
      return DEFAULT_CHUNK_SIZE;
  }
}

// ============================================================================
// Blob Manifest API
// ============================================================================

/**
 * Fetches the node blob manifest from the API.
 * The manifest indicates where node data is stored (API or blob storage)
 * and provides the blob URL if applicable.
 * 
 * @param modelId - The model ID
 * @param cachedEtag - Optional cached ETag to send in If-None-Match header for validation
 * @returns Object with manifest, ETag from response, and notModified flag (true if 304, false if 200)
 */
export async function fetchNodeBlobManifest(
  modelId: string,
  cachedEtag?: string | null
): Promise<{ manifest: NodeBlobManifest | null; etag: string | null; notModified: boolean }> {
  const url = cachedEtag ? `/api/models/${modelId}/nodes-mp/manifest` : `/api/models/${modelId}/nodes-mp/manifest?_t=${Date.now()}`;

  try {
    const headers: Record<string, string> = { 'Accept': 'application/json' };
    if (cachedEtag != null && cachedEtag !== '') {
      headers['If-None-Match'] = cachedEtag;
    }
    const response = await fetch(url, { method: 'GET', headers, cache: 'no-store' });

    if (response.status === 304) {
      return { manifest: null, etag: cachedEtag || null, notModified: true };
    }

    if (response.status === 200) {
      const etag = response.headers.get('ETag') || null;
      const manifest = await response.json() as NodeBlobManifest;
      return { manifest, etag, notModified: false };
    }

    return { manifest: null, etag: null, notModified: false };
  } catch {
    return { manifest: null, etag: null, notModified: false };
  }
}

/**
 * Checks if blob storage should be used for downloading.
 * Returns true if:
 * - Blob storage provider is configured (not 'api')
 * - The manifest indicates data is in blob storage
 */
export function shouldUseBlobStorage(): boolean {
  const provider = getBlobStorageProvider();
  return provider !== 'api';
}

/**
 * Simple metadata stored alongside model files.
 */
export interface ModelMetadata {
  modelId: string;
  totalSize: number;
  etag: string | null;
  downloadedAt: string;
}

/**
 * Download progress information.
 */
export interface DownloadProgress {
  /** Current phase of the download */
  phase: 'checking' | 'downloading' | 'writing' | 'complete' | 'cached';
  /** Model ID being downloaded */
  modelId: string;
  /** Total bytes expected */
  totalBytes: number;
  /** Bytes downloaded so far */
  bytesDownloaded: number;
  /** Current chunk being downloaded */
  currentChunk: number;
  /** Total number of chunks */
  totalChunks: number;
  /** Percentage complete (0-100) */
  percent: number;
  /** Whether download is complete */
  complete: boolean;
}

/**
 * Download source type - affects optimal download strategy.
 * Maps to BlobStorageProvider but kept for backwards compatibility.
 */
export type DownloadSource = BlobStorageProvider;

/**
 * Download options.
 */
export interface DownloadOptions {
  /** 
   * Chunk size in bytes.
   * If not specified, uses optimal size based on source:
   * - Azure: 10MB (frequent progress, efficient serving)
   * - S3: 5MB (aligns with multipart upload part size)
   * - API: 10MB (default)
   */
  chunkSize?: number;
  /** Progress callback */
  onProgress?: (progress: DownloadProgress) => void;
  /** Force re-download even if cached */
  forceDownload?: boolean;
  /**
   * Download source - affects default chunk size.
   * Default: 'api'
   */
  source?: DownloadSource;
  /**
   * Maximum concurrent downloads for parallel operations.
   * Default: 2
   */
  maxConcurrent?: number;
  /**
   * Use row-based pagination instead of byte ranges.
   * Much more efficient for API endpoints (server only queries requested rows).
   * - First batch: 10,000 rows (to get total count quickly)
   * - Subsequent batches: 100,000 rows each
   * Default: false
   */
  useRowPagination?: boolean;
  /**
   * Number of rows in first batch (to get total count).
   * Default: 10,000
   */
  firstBatchRows?: number;
  /**
   * Number of rows in subsequent batches.
   * Default: 100,000
   */
  batchRows?: number;
}

/**
 * Download result.
 */
export interface DownloadResult {
  /** Whether download was successful */
  success: boolean;
  /** Model ID */
  modelId: string;
  /** Total bytes downloaded */
  totalBytes: number;
  /** Whether data was loaded from cache */
  fromCache: boolean;
  /** Number of chunks downloaded */
  chunksDownloaded: number;
  /** Error message if failed */
  error?: string;
}

/**
 * Saves metadata for a model.
 */
async function saveMetadata(metadata: ModelMetadata): Promise<void> {
  const root = await getOPFSRoot();
  const fileName = `metadata-${metadata.modelId}.json`;
  const fileHandle = await root.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(JSON.stringify(metadata));
  } finally {
    await writable.close();
  }
}

/**
 * Reads metadata for a model.
 */
async function readMetadata(modelId: string): Promise<ModelMetadata | null> {
  try {
    const root = await getOPFSRoot();
    const fileName = `metadata-${modelId}.json`;
    const fileHandle = await root.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    const text = await file.text();
    return JSON.parse(text) as ModelMetadata;
  } catch {
    return null;
  }
}

/**
 * Deletes metadata for a model.
 */
async function deleteMetadata(modelId: string): Promise<void> {
  try {
    const root = await getOPFSRoot();
    const fileName = `metadata-${modelId}.json`;
    await root.removeEntry(fileName);
  } catch {
    // File doesn't exist, ignore
  }
}

// ============================================================================
// Row-Based Pagination (for API - efficient parallel downloads)
// ============================================================================

interface RowRangeResult {
  data: ArrayBuffer;
  /** Start row index */
  startRow: number;
  /** End row index (inclusive) */
  endRow: number;
  /** Total row count from server */
  totalRows: number;
}

/**
 * Downloads a range of rows using X-Row-Range header.
 * Server only queries and serializes the requested rows.
 */
async function downloadRowRange(url: string, startRow: number, endRow: number): Promise<RowRangeResult> {
  console.log(`[ModelDownloader] Requesting rows ${startRow}-${endRow}`);

  const response = await fetch(url, {
    headers: {
      'Accept': 'application/x-memorypack',
      'X-Row-Range': `${startRow}-${endRow}`,
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Row range request failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.arrayBuffer();

  // Parse X-Content-Row-Range: start-end/total
  const rowRangeHeader = response.headers.get('X-Content-Row-Range');
  let totalRows = 0;
  let actualStart = startRow;
  let actualEnd = endRow;

  if (rowRangeHeader) {
    const match = rowRangeHeader.match(/(\d+)-(\d+)\/(\d+)/);
    if (match) {
      actualStart = parseInt(match[1], 10);
      actualEnd = parseInt(match[2], 10);
      totalRows = parseInt(match[3], 10);
    }
  }

  // Fallback to X-Total-Count header
  if (totalRows === 0) {
    const totalCountHeader = response.headers.get('X-Total-Count');
    if (totalCountHeader) {
      totalRows = parseInt(totalCountHeader, 10);
    }
  }

  console.log(`[ModelDownloader] Got rows ${actualStart}-${actualEnd} of ${totalRows} (${data.byteLength} bytes)`);

  return { data, startRow: actualStart, endRow: actualEnd, totalRows };
}

/**
 * Downloads model data using row-based pagination with parallel requests.
 * Much more efficient than byte-range requests for API endpoints.
 * 
 * Flow:
 * 1. First request: small batch to get total row count
 * 2. Calculate remaining row ranges
 * 3. Download remaining ranges in parallel
 * 
 * @param url - API endpoint URL
 * @param firstBatchSize - Size of first batch (default: 10,000 rows)
 * @param batchSize - Size of subsequent batches (default: 100,000 rows)
 * @param maxConcurrent - Max parallel requests (default: 4)
 * @param reportProgress - Progress callback
 */
async function downloadWithRowPagination(
  url: string,
  firstBatchSize: number,
  batchSize: number,
  maxConcurrent: number,
  reportProgress: (progress: Partial<DownloadProgress>) => void
): Promise<ArrayBuffer> {
  console.log(`[ModelDownloader] Row pagination download starting`);
  console.log(`[ModelDownloader] First batch: ${firstBatchSize} rows, subsequent: ${batchSize} rows, max concurrent: ${maxConcurrent}`);

  // First request to get total count
  const firstResult = await downloadRowRange(url, 0, firstBatchSize - 1);
  const totalRows = firstResult.totalRows;

  if (totalRows === 0) {
    console.log(`[ModelDownloader] No rows to download`);
    return firstResult.data;
  }

  // Calculate how many batches we need
  const remainingRows = totalRows - firstBatchSize;
  const remainingBatches = remainingRows > 0 ? Math.ceil(remainingRows / batchSize) : 0;
  const totalBatches = 1 + remainingBatches;

  console.log(`[ModelDownloader] Total: ${totalRows} rows in ${totalBatches} batches`);

  // Store results in order
  const batchResults: ArrayBuffer[] = new Array(totalBatches);
  batchResults[0] = firstResult.data;

  let completedBatches = 1;
  let rowsDownloaded = Math.min(firstBatchSize, totalRows);

  // Report first batch progress
  reportProgress({
    phase: 'downloading',
    totalBytes: totalRows, // Using rows as "bytes" for progress
    bytesDownloaded: rowsDownloaded,
    currentChunk: completedBatches,
    totalChunks: totalBatches,
    percent: Math.round((rowsDownloaded / totalRows) * 100),
  });

  // If only one batch needed, we're done
  if (totalBatches === 1) {
    console.log(`[ModelDownloader] Single batch download complete: ${rowsDownloaded} rows`);
    return batchResults[0];
  }

  // Download remaining batches in parallel
  const downloadBatch = async (batchIndex: number): Promise<void> => {
    const startRow = firstBatchSize + (batchIndex - 1) * batchSize;
    const endRow = Math.min(startRow + batchSize - 1, totalRows - 1);

    console.log(`[ModelDownloader] Batch ${batchIndex + 1}/${totalBatches}: rows ${startRow}-${endRow}`);

    const result = await downloadRowRange(url, startRow, endRow);
    batchResults[batchIndex] = result.data;
    completedBatches++;
    rowsDownloaded += (result.endRow - result.startRow + 1);

    const percent = Math.round((rowsDownloaded / totalRows) * 100);
    reportProgress({
      phase: 'downloading',
      totalBytes: totalRows,
      bytesDownloaded: rowsDownloaded,
      currentChunk: completedBatches,
      totalChunks: totalBatches,
      percent,
    });

    console.log(`[ModelDownloader] Batch ${batchIndex + 1}/${totalBatches} done (${percent}%)`);
  };

  // Process remaining batches with concurrency limit
  const pendingPromises: Promise<void>[] = [];
  let nextBatch = 1;

  const startNextBatch = (): Promise<void> | null => {
    if (nextBatch >= totalBatches) return null;
    const batchIndex = nextBatch++;
    return downloadBatch(batchIndex);
  };

  // Start initial parallel batch
  for (let i = 0; i < Math.min(maxConcurrent, totalBatches - 1); i++) {
    const promise = startNextBatch();
    if (promise) pendingPromises.push(promise);
  }

  // Process as batches complete
  while (pendingPromises.length > 0) {
    const completedIndex = await Promise.race(
      pendingPromises.map((p, i) => p.then(() => i))
    );
    pendingPromises.splice(completedIndex, 1);

    const nextPromise = startNextBatch();
    if (nextPromise) {
      pendingPromises.push(nextPromise);
    }
  }

  console.log(`[ModelDownloader] Row pagination complete: ${rowsDownloaded} rows in ${totalBatches} batches`);

  // Combine all batch results
  return combineArrayBuffers(batchResults);
}

/**
 * Downloads a model's node data and stores it in OPFS.
 * 
 * Automatically uses blob storage if configured, otherwise uses API.
 * 
 * @param modelId - The model ID to download
 * @param options - Download options
 * @returns Download result
 */
export async function downloadModel(
  modelId: string,
  options: DownloadOptions = {}
): Promise<DownloadResult> {
  const {
    onProgress,
    forceDownload = false,
    source = 'api',
  } = options;

  // Select optimal chunk size based on source if not explicitly specified
  const chunkSize = options.chunkSize ?? (source === 'azure' ? AZURE_CHUNK_SIZE : DEFAULT_CHUNK_SIZE);

  console.log(`[ModelDownloader] downloadModel called for ${modelId}`, {
    source,
    chunkSize,
    forceDownload,
  });

  const reportProgress = (progress: Partial<DownloadProgress>) => {
    const fullProgress: DownloadProgress = {
      phase: 'downloading',
      modelId,
      totalBytes: 0,
      bytesDownloaded: 0,
      currentChunk: 0,
      totalChunks: 0,
      percent: 0,
      complete: false,
      ...progress,
    };
    console.log(`[ModelDownloader] Progress:`, fullProgress);
    onProgress?.(fullProgress);
  };

  try {
    const isCached = await isModelCached(modelId);
    const metadata = await readMetadata(modelId);

    if (!forceDownload && isCached && metadata?.etag) {
      reportProgress({ phase: 'checking' });
      const manifestResult = await fetchNodeBlobManifest(modelId, metadata.etag);
      if (manifestResult.notModified) {
        reportProgress({
          phase: 'cached',
          totalBytes: metadata.totalSize || 0,
          bytesDownloaded: metadata.totalSize || 0,
          currentChunk: 0,
          totalChunks: 0,
          percent: 100,
          complete: true,
        });
        return {
          success: true,
          modelId,
          totalBytes: metadata.totalSize || 0,
          fromCache: true,
          chunksDownloaded: 0,
        };
      }
    }

    reportProgress({ phase: 'checking' });

    const blobProvider = getBlobStorageProvider();
    const useBlobStorage = blobProvider !== 'api';

    if (useBlobStorage) {
      const manifestResult = await fetchNodeBlobManifest(modelId, metadata?.etag || null);
      const manifest = manifestResult.manifest;

      if (manifestResult.notModified && isCached) {
        reportProgress({
          phase: 'cached',
          totalBytes: metadata?.totalSize || 0,
          bytesDownloaded: metadata?.totalSize || 0,
          currentChunk: 0,
          totalChunks: 0,
          percent: 100,
          complete: true,
        });
        return {
          success: true,
          modelId,
          totalBytes: metadata?.totalSize || 0,
          fromCache: true,
          chunksDownloaded: 0,
        };
      }

      if (manifest && manifest.storageType === 'blob' && manifest.blobUrl) {
        const result = await downloadFromBlobStorage(modelId, manifest.blobUrl, {
          onProgress,
          forceDownload,
          maxConcurrent: options.maxConcurrent,
          chunkSize: options.chunkSize ?? getOptimalChunkSize(blobProvider),
        });
        
        if (manifestResult.etag && result.success && metadata) {
          await saveMetadata({ ...metadata, etag: manifestResult.etag });
        }
        
        return result;
      }
    }

    // Fall through to API download using row pagination (most efficient for API)
    const url = `/api/models/${modelId}/nodes-mp`;

    const maxConcurrent = options.maxConcurrent ?? 2;
    const firstBatchRows = options.firstBatchRows ?? 10000;
    const batchRows = options.batchRows ?? 100000;

    console.log(`[ModelDownloader] Using parallel row pagination (${maxConcurrent} concurrent)`);
    console.log(`[ModelDownloader] First batch: ${firstBatchRows} rows, subsequent: ${batchRows} rows`);
    console.log(`[ModelDownloader] URL: ${url}`);
    const buffer = await downloadWithRowPagination(url, firstBatchRows, batchRows, maxConcurrent, reportProgress);
    const chunksDownloaded = Math.ceil(buffer.byteLength / (batchRows * 450)); // Rough estimate

    console.log(`[ModelDownloader] Downloaded ${buffer.byteLength} bytes`);

    // Write to OPFS
    reportProgress({
      phase: 'writing',
      totalBytes: buffer.byteLength,
      bytesDownloaded: buffer.byteLength,
      currentChunk: chunksDownloaded,
      totalChunks: chunksDownloaded,
      percent: 100,
    });

    await writeModelFile(modelId, buffer);

    let etag: string | null = null;
    if (!useBlobStorage) {
      const manifestResult = await fetchNodeBlobManifest(modelId);
      etag = manifestResult.etag;
    }
    
    await saveMetadata({
      modelId,
      totalSize: buffer.byteLength,
      etag: etag,
      downloadedAt: new Date().toISOString(),
    });

    reportProgress({
      phase: 'complete',
      totalBytes: buffer.byteLength,
      bytesDownloaded: buffer.byteLength,
      currentChunk: chunksDownloaded,
      totalChunks: chunksDownloaded,
      percent: 100,
      complete: true,
    });

    return {
      success: true,
      modelId,
      totalBytes: buffer.byteLength,
      fromCache: false,
      chunksDownloaded,
    };

  } catch (error) {
    const isAbort =
      (error as any)?.name === 'AbortError' ||
      (error instanceof Error && /aborted|canceled/i.test(error.message));
    if (!isAbort) {
      console.error(`[ModelDownloader] Download failed for model ${modelId}:`, error);
    }
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return {
      success: false,
      modelId,
      totalBytes: 0,
      fromCache: false,
      chunksDownloaded: 0,
      error: errorMessage,
    };
  }
}

/**
 * Combines multiple ArrayBuffers into a single buffer.
 */
function combineArrayBuffers(buffers: ArrayBuffer[]): ArrayBuffer {
  const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;

  for (const buffer of buffers) {
    result.set(new Uint8Array(buffer), offset);
    offset += buffer.byteLength;
  }

  return result.buffer;
}

/**
 * Checks if a model is cached and returns its status.
 */
export async function getModelCacheStatus(modelId: string): Promise<{
  cached: boolean;
  metadata: ModelMetadata | null;
  fileSize: number | null;
}> {
  const cached = await isModelCached(modelId);
  const metadata = await readMetadata(modelId);
  const fileSize = cached ? await getModelFileSize(modelId) : null;

  return {
    cached,
    metadata,
    fileSize,
  };
}

/**
 * Invalidates cached model data.
 */
export async function invalidateModelCache(modelId: string): Promise<void> {
  await deleteModelFile(modelId);
  await deleteMetadata(modelId);
}

/**
 * Downloads from API using row-based pagination with parallel requests.
 * MOST EFFICIENT for API - server only queries and serializes requested rows.
 * 
 * Flow:
 * 1. First request: 10k rows (get total count quickly)
 * 2. Remaining requests: 100k rows each (in parallel)
 * 
 * For 445k rows: 1 small + 5 parallel = ~6 requests total
 * Each request only does work for its rows (no wasted serialization!)
 */
export async function downloadFromAPIParallel(
  modelId: string,
  options: Omit<DownloadOptions, 'source' | 'useRowPagination'> = {}
): Promise<DownloadResult> {
  console.log(`[ModelDownloader] Parallel row pagination download for ${modelId}`);
  return downloadModel(modelId, {
    ...options,
    source: 'api',
    useRowPagination: true,
    firstBatchRows: options.firstBatchRows ?? 10000,
    batchRows: options.batchRows ?? 100000,
    maxConcurrent: options.maxConcurrent ?? 2,
  });
}

/**
 * Downloads from blob storage (Azure Blob Storage, S3, etc.) using the configured provider.
 * Use this when data is stored in external blob storage.
 * 
 * Benefits:
 * - Optimized chunk sizes per provider
 * - Cloud storage serves byte ranges efficiently (no re-computation)
 * - Resumable (can restart from last successful chunk)
 * - Parallel downloads for speed
 * 
 * @param modelId - The model ID
 * @param blobUrl - The pre-signed URL to the blob
 * @param options - Download options
 */
export async function downloadFromBlobStorage(
  modelId: string,
  blobUrl: string,
  options: Omit<DownloadOptions, 'source'> = {}
): Promise<DownloadResult> {
  const provider = getBlobStorageProvider();
  const blobService = getBlobStorageService();
  const chunkSize = options.chunkSize ?? getOptimalChunkSize(provider);

  console.log(`[ModelDownloader] Blob storage download for ${modelId}`);
  console.log(`[ModelDownloader] Provider: ${provider}, URL: ${blobUrl}`);

  const { onProgress, forceDownload = false } = options;

  const reportProgress = (progress: Partial<DownloadProgress>) => {
    const fullProgress: DownloadProgress = {
      phase: 'downloading',
      modelId,
      totalBytes: 0,
      bytesDownloaded: 0,
      currentChunk: 0,
      totalChunks: 0,
      percent: 0,
      complete: false,
      ...progress,
    };
    onProgress?.(fullProgress);
  };

  try {
    // Check cache first
    const isCached = await isModelCached(modelId);
    if (!forceDownload && isCached) {
      const metadata = await readMetadata(modelId);
      const fileSize = await getModelFileSize(modelId);

      if (metadata && fileSize && fileSize === metadata.totalSize) {
        console.log(`[ModelDownloader] Model ${modelId} found in OPFS cache (${fileSize} bytes)`);
        reportProgress({
          phase: 'cached',
          totalBytes: fileSize,
          bytesDownloaded: fileSize,
          percent: 100,
          complete: true,
        });
        return {
          success: true,
          modelId,
          totalBytes: fileSize,
          fromCache: true,
          chunksDownloaded: 0,
        };
      }
    }

    reportProgress({ phase: 'checking' });

    // Download using blob storage service
    const buffer = await blobService.downloadBlob(blobUrl, {
      chunkSize,
      maxConcurrency: options.maxConcurrent ?? 2,
      onProgress: (blobProgress: BlobDownloadProgress) => {
        reportProgress({
          phase: blobProgress.phase === 'complete' ? 'writing' : 'downloading',
          totalBytes: blobProgress.totalBytes,
          bytesDownloaded: blobProgress.bytesDownloaded,
          currentChunk: blobProgress.currentChunk,
          totalChunks: blobProgress.totalChunks,
          percent: blobProgress.percent,
        });
      },
    });

    // Write to OPFS
    await writeModelFile(modelId, buffer);
    console.log(`[ModelDownloader] Saved model ${modelId} to OPFS (${buffer.byteLength} bytes)`);

    // Save metadata
    await saveMetadata({
      modelId,
      totalSize: buffer.byteLength,
      etag: null,
      downloadedAt: new Date().toISOString(),
    });

    reportProgress({
      phase: 'complete',
      totalBytes: buffer.byteLength,
      bytesDownloaded: buffer.byteLength,
      percent: 100,
      complete: true,
    });

    return {
      success: true,
      modelId,
      totalBytes: buffer.byteLength,
      fromCache: false,
      chunksDownloaded: Math.ceil(buffer.byteLength / chunkSize),
    };

  } catch (error) {
    const isAbort =
      (error as any)?.name === 'AbortError' ||
      (error instanceof Error && /aborted|canceled/i.test(error.message));
    if (!isAbort) {
      console.error(`[ModelDownloader] Blob storage download failed for model ${modelId}:`, error);
    }
    return {
      success: false,
      modelId,
      totalBytes: 0,
      fromCache: false,
      chunksDownloaded: 0,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Downloads from S3 using optimized Range requests.
 * Convenience wrapper for downloadFromBlobStorage.
 * 
 * @param modelId - The model ID
 * @param presignedUrl - The pre-signed S3 URL
 * @param options - Download options
 */
export async function downloadFromS3(
  modelId: string,
  presignedUrl: string,
  options: Omit<DownloadOptions, 'source'> = {}
): Promise<DownloadResult> {
  console.log(`[ModelDownloader] S3 download for ${modelId}`);
  return downloadFromBlobStorage(modelId, presignedUrl, {
    ...options,
    chunkSize: options.chunkSize ?? S3_CHUNK_SIZE,
  });
}

/**
 * Reads model data from OPFS.
 * Returns null if not cached.
 */
export async function readModelData(modelId: string): Promise<ArrayBuffer | null> {
  return readModelFile(modelId);
}

/**
 * Clears all model caches (OPFS and service worker Cache API).
 * Useful for debugging.
 */
export async function clearAllCaches(): Promise<void> {
  console.log('[ModelDownloader] Clearing all caches...');

  // Clear OPFS - try each file individually
  try {
    const root = await getOPFSRoot();
    const entries: string[] = [];
    // @ts-expect-error - entries() is available
    for await (const [name] of root.entries()) {
      entries.push(name);
    }

    let deleted = 0;
    let failed = 0;
    for (const name of entries) {
      try {
        await root.removeEntry(name);
        console.log(`[ModelDownloader] Deleted OPFS entry: ${name}`);
        deleted++;
      } catch (err) {
        console.warn(`[ModelDownloader] Failed to delete ${name}:`, err);
        failed++;
      }
    }
    console.log(`[ModelDownloader] OPFS: deleted ${deleted}, failed ${failed}`);

    if (failed > 0) {
      console.log('[ModelDownloader] Some files may be locked by workers. Try closing all tabs and reopening.');
    }
  } catch (error) {
    console.error('[ModelDownloader] Failed to clear OPFS:', error);
  }

  // Clear service worker cache
  try {
    const cacheNames = await caches.keys();
    for (const cacheName of cacheNames) {
      await caches.delete(cacheName);
      console.log(`[ModelDownloader] Deleted cache: ${cacheName}`);
    }
    console.log(`[ModelDownloader] Cleared ${cacheNames.length} service worker caches`);
  } catch (error) {
    console.error('[ModelDownloader] Failed to clear service worker caches:', error);
  }

  console.log('[ModelDownloader] All caches cleared. Reload the page to re-download data.');
}

/**
 * Force invalidate and re-download a specific model.
 * This bypasses cache checks.
 */
export async function forceRedownload(modelId: string): Promise<DownloadResult> {
  console.log(`[ModelDownloader] Force re-downloading model ${modelId}...`);

  // Try to delete the cached file first
  try {
    await deleteModelFile(modelId);
    await deleteMetadata(modelId);
    console.log(`[ModelDownloader] Deleted cached files for ${modelId}`);
  } catch (err) {
    console.warn(`[ModelDownloader] Could not delete cache for ${modelId}:`, err);
  }

  // Download with forceDownload flag
  return downloadModel(modelId, { forceDownload: true });
}

// ============================================================================
// Debug Interface
// ============================================================================

/**
 * Debug interface for model cache inspection.
 * Available at window.ModelCache in the browser console.
 */
export interface ModelCacheDebug {
  /** List all cached models with their sizes */
  list: () => Promise<Array<{ modelId: string; size: number; metadata: ModelMetadata | null; }>>;
  /** Get detailed status for a specific model */
  status: (modelId: string) => Promise<{ cached: boolean; metadata: ModelMetadata | null; fileSize: number | null; }>;
  /** Clear all caches (OPFS + service worker) */
  clearAll: () => Promise<void>;
  /** Clear a specific model's cache */
  clear: (modelId: string) => Promise<void>;
  /** Force re-download a model */
  redownload: (modelId: string) => Promise<DownloadResult>;
  /** Download with row pagination (recommended for API) */
  downloadParallel: (modelId: string, options?: Partial<DownloadOptions>) => Promise<DownloadResult>;
  /** Check if OPFS is supported */
  isSupported: () => boolean;
  /** Get storage statistics */
  stats: () => Promise<{ modelCount: number; totalSize: number; totalSizeMB: string; }>;
}

// Build the debug interface
const modelCacheDebug: ModelCacheDebug = {
  async list() {
    const root = await getOPFSRoot();
    const models: Array<{ modelId: string; size: number; metadata: ModelMetadata | null; }> = [];

    // @ts-expect-error - values() exists at runtime but TypeScript definitions are incomplete
    for await (const entry of root.values()) {
      const handle = entry as FileSystemHandle;
      if (handle.kind === 'file' && handle.name.startsWith('model-') && handle.name.endsWith('.bin')) {
        const modelId = handle.name.slice('model-'.length, -'.bin'.length);
        const file = await (handle as FileSystemFileHandle).getFile();
        const metadata = await readMetadata(modelId);
        models.push({ modelId, size: file.size, metadata });
      }
    }

    // Log as table for easy viewing
    console.table(models.map(m => ({
      modelId: m.modelId,
      sizeMB: (m.size / 1024 / 1024).toFixed(2) + ' MB',
      downloadedAt: m.metadata?.downloadedAt || 'unknown',
    })));

    return models;
  },

  status: getModelCacheStatus,
  clearAll: clearAllCaches,
  clear: invalidateModelCache,
  redownload: forceRedownload,
  downloadParallel: downloadFromAPIParallel,

  isSupported() {
    const supported = 'storage' in navigator && 'getDirectory' in navigator.storage;
    console.log(`OPFS supported: ${supported}`);
    return supported;
  },

  async stats() {
    const models = await this.list();
    const totalSize = models.reduce((sum, m) => sum + m.size, 0);
    const stats = {
      modelCount: models.length,
      totalSize,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2) + ' MB',
    };
    console.log('Model Cache Stats:', stats);
    return stats;
  },
};

// Expose on window for console access
if (typeof window !== 'undefined') {
  (window as any).ModelCache = modelCacheDebug;

  // Keep legacy __modelDownloader for backwards compatibility
  (window as any).__modelDownloader = {
    clearAllCaches,
    invalidateModelCache,
    getModelCacheStatus,
    downloadModel,
    downloadFromAPIParallel,
    downloadFromBlobStorage,
    downloadFromS3,
    forceRedownload,
    getBlobStorageProvider, // utility to check current provider
  };
}

// Type declarations for window globals
export { };
declare global {
  interface Window {
    ModelCache: ModelCacheDebug;
    __modelDownloader: {
      clearAllCaches: typeof clearAllCaches;
      invalidateModelCache: typeof invalidateModelCache;
      getModelCacheStatus: typeof getModelCacheStatus;
      downloadModel: typeof downloadModel;
      downloadFromAPIParallel: typeof downloadFromAPIParallel;
      downloadFromBlobStorage: typeof downloadFromBlobStorage;
      downloadFromS3: typeof downloadFromS3;
      forceRedownload: typeof forceRedownload;
      getBlobStorageProvider: typeof getBlobStorageProvider;
    };
  }
}

